<template>
  <div class="py-3">
    <div class="p-toolbar">
      <div class="p-toolbar-item p-search">
        <p-search-input
          v-model="search"
          :items="searchSuggestions"
        ></p-search-input>
      </div>
      <div v-if="hasAccess()" class="p-toolbar-item p-right">
        <v-btn
          class="p-btn-rounded mr-4"
          large
          rounded
          @click.stop="openEditDialog(null)"
        >
          {{ $t('season.createSeason') }}
        </v-btn>
      </div>
    </div>

    <v-row>
      <v-col v-if="seasons.length > 0">
        <v-data-table
          :headers="tableHeaders"
          :items="seasonsComputed"
          :search="search"
          :page.sync="page"
          :items-per-page="itemsPerPage"
          class="p-no-borders"
          hide-default-footer
          @page-count="pageCount = $event"
        >
          <template v-slot:[`item.changed`]="{ item }">
            <v-tooltip v-if="item.changed" bottom>
              <template v-slot:activator="{ on }">
                <v-btn icon v-on="on">
                  <span v-if="item.changed" class="item-changed"></span>
                </v-btn>
              </template>
              {{ $t('season.changedTooltip') }}
            </v-tooltip>
          </template>
          <template v-if="hasAccess()" v-slot:[`item.actions`]="{ item }">
            <div class="p-table-row-actions">
              <p-btn-icon type="exportXlsx" @click="exportXlsx(item)"></p-btn-icon>
              <p-btn-icon type="edit" @click="openEditDialog(item)"></p-btn-icon>
              <p-btn-icon type="delete" @click="openDeleteDialog(item)"></p-btn-icon>
            </div>
          </template>
        </v-data-table>

        <v-pagination
          v-model="page"
          :length="pageCount"
          :total-visible="15"
          class="mt-5"
        ></v-pagination>
      </v-col>

      <v-col v-else>
        <v-divider class="mb-16"></v-divider>
        <p-no-data
          :text="$t('season.noSeasons')"
          :btn-text="$t('season.createSeason')"
          :hide-btn="!hasAccess()"
          img="event.png"
          @btn-click="openEditDialog(null)"
        ></p-no-data>
      </v-col>
    </v-row>

    <season-edit-dialog
      :dialog.sync="editDialog"
      :season="editSeason"
    ></season-edit-dialog>

    <p-delete-dialog
      :dialog.sync="deleteDialog"
      :item="deletedSeason"
      :item-data="deletedSeasonData"
      :load-relations="loadRelations"
      :delete-item="deleteSeason"
      @itemDeleted="onItemDeleted"
    ></p-delete-dialog>
  </div>
</template>


<script>
import { mapGetters, mapActions } from 'vuex'

import fileHelper from '@/helpers/file.helper'

import PSearchInput from '@/components/PSearchInput'
import PNoData from '@/components/PNoData'
import PBtnIcon from '@/components/PBtnIcon'
import PDeleteDialog from '@/components/PDeleteDialog'

import SeasonEditDialog from './SeasonEditDialog'

import snackbarMixin from '@/mixins/snackbar.mixin'
import pageMixin from '@/mixins/page.mixin'
import accessMixin from '@/mixins/access.mixin'

import eventBus from '@/eventBus'


export default {
  components: {
    PSearchInput,
    PNoData,
    PBtnIcon,
    PDeleteDialog,
    SeasonEditDialog
  },


  mixins: [
    snackbarMixin,
    pageMixin,
    accessMixin
  ],


  data () {
    return {
      search: '',
      editDialog: false,
      editSeason: null,
      page: 1,
      pageCount: 0,
      itemsPerPage: 15,
      deleteDialog: false,
      deletedSeason: null,
      deletedSeasonData: null
    }
  },


  computed: {
    ...mapGetters('season', ['seasons']),
    ...mapGetters('changes', ['isItemChanged']),

    seasonsComputed () {
      return this.seasons.map(season => {
        return {
          ...season,
          changed: this.isItemChanged(season.id)
        }
      })
    },

    tableHeaders () {
      return [
        { text: this.$t('season.name'), value: 'name' },
        { text: '', value: 'changed' },
        { text: '', value: 'actions', align: 'end', sortable: false, filterable: false }
      ]
    },

    searchSuggestions () {
      const items = this.seasons.map(x => x.name)
      return items
    }
  },


  methods: {
    ...mapActions('season', ['loadSeasons', 'loadRelations', 'deleteSeason']),
    ...mapActions('excel', ['getProjectsWithEventsXlsx']),

    async searchSeasons (val) {
      this.search = val
    },

    initEventBus () {
      eventBus.$on('goToEntity', async (entityTitle) => {
        this.searchSeasons(entityTitle)
      })

      eventBus.$on('loadEntities', async () => {
        await this.loadSeasons()
      })
    },


    async exportXlsx (season) {
      try {
        const xlsx = await this.getProjectsWithEventsXlsx(season.id)
        const blob = new Blob([xlsx], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
        fileHelper.downloadFile(blob, season.name || 'season')
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
      }
    },

    openEditDialog (season) {
      this.editSeason = season
      this.editDialog = true
    },

    openDeleteDialog (season) {
      try {
        if (season) {
          this.deletedSeason = season
          this.deletedSeasonData = {
            type: this.$t('entity.season'),
            title: season.name
          }
        }
        this.deleteDialog = true
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
      }
    },

    onItemDeleted () {
      this.showSnackbarSuccess(this.$t('season.seasonDeleted'))
    }
  },

  async created () {
    this.initEventBus()
    try {
      this.startPageLoading()
      await this.loadSeasons()
      if (this.$router.currentRoute.params?.goto) {
        await this.searchSeasons(this.$router.currentRoute.params?.goto)
      }
    } catch (error) {
      this.showSnackbarError()
      this.$log.error(error)
    } finally {
      this.stopPageLoading()
    }
  }
}
</script>
